import React, { useState, useEffect, useRef } from "react";

import dayjs from "dayjs";
import JSZip from "jszip";

import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";

import { CustomPagination } from "../customPagination";

import { useUserAuth } from "../contexts/authContext";

import MapPlaybackFileCreator from "./mapPlaybackFileCreator";
import MapPlaybackFileViewer from "./mapPlaybackFileViewer";
import { getPlaybackFileValid } from "./mapPlaybackUtil";

const MapPlaybackDatagrid = () => {
    const { user, handleFailedFetch, socket } = useUserAuth();

    const [previousSubmissions, setPreviousSubmissions] = useState([]);

    const [viewPlaybackFileData, setViewPlaybackFileData] = useState(null);
    const [creatingSubmission, setCreatingSubmission] = useState(false);

    const fileInputRef = useRef(null);

    const columns = [
        {
            field: "date_created",
            headerName: "Date Created",
            minWidth: 175,
            flex: 1,
            editable: false,
            valueFormatter: (value) => `${dayjs(value).format("MMMM D, YYYY [at] h:mm a")}`
        },
        {
            field: "date_updated",
            headerName: "Date Updated",
            minWidth: 175,
            flex: 1,
            editable: false,
            valueFormatter: (value) => {
                if (value) {
                    return `${dayjs(value).format("MMMM D, YYYY [at] h:mm a")}`;
                } else {
                    return "";
                }
            }
        },
        {
            field: "error_message",
            headerName: "Error Message",
            minWidth: 175,
            flex: 1,
            editable: false,
            renderCell: (params) => <span style={{ color: "red", fontWeight: "bold" }}>{params.value}</span>
        },
        {
            type: "actions",
            field: "actions",
            minWidth: 125,
            headerName: "Actions",
            cellClassName: "actions",
            getActions: ({ row }) => {
                const options = [];

                if (row.playback_file && !row.error_message) {
                    options.push(
                        <Tooltip key={1} title="View file">
                            <GridActionsCellItem icon={<VisibilityIcon />} label="View" onClick={() => handleViewRecordButtonClick(row)} />
                        </Tooltip>
                    );
                    options.push(
                        <Tooltip key={2} title="Download file">
                            <GridActionsCellItem icon={<DownloadIcon />} label="Download" onClick={() => handleDownloadButtonClick(row)} />
                        </Tooltip>
                    );
                }
                if (row.playback_file || row.error_message) {
                    options.push(
                        <Tooltip key={3} title="Delete record">
                            <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={() => handleDeleteButtonRecordClick(row)} />
                        </Tooltip>
                    );
                }
                return options;
            }
        }
    ];

    useEffect(() => {
        if (!user || !socket) return;
        getPreviousSubmissions();

        socket.on("playback_update", () => {
            getPreviousSubmissions();
        });
        return () => {
            socket.off("playback_update");
        };
    }, [user, socket]);

    const getPreviousSubmissions = () => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch(`api/map_playback/get_submissions/${user.id}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((records) => {
                setPreviousSubmissions(records);
            })
            .catch((err) => {
                handleFailedFetch(err);
            });
    };

    const handleViewRecordButtonClick = (playbackRecord) => {
        try {
            const bytes = new Uint8Array(playbackRecord.playback_file.data);
            const decoder = new TextDecoder("utf-8");

            const jsonString = decoder.decode(bytes);
            const json = JSON.parse(jsonString);

            const isValidFormat = getPlaybackFileValid(json);
            if (isValidFormat === "") {
                setViewPlaybackFileData(json);
            } else {
                alert(isValidFormat);
            }
        } catch (error) {
            console.error("Error parsing playback file:", error);
        }
    };
    const handleDownloadButtonClick = async (playbackRecord) => {
        try {
            const zip = new JSZip();
            const byteArray = new Uint8Array(playbackRecord.playback_file.data);

            const fileName = `${playbackRecord.playback_uuid}.json`;
            zip.file(fileName, byteArray);

            const zipContent = await zip.generateAsync({
                type: "blob",
                compression: "DEFLATE",
                compressionOptions: { level: 9 }
            });
            const url = window.URL.createObjectURL(zipContent);
            const link = document.createElement("a");
            link.href = url;

            const zipName = `${playbackRecord.playback_uuid}.zip`;
            link.setAttribute("download", zipName);

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed:", error);
        }
    };
    const handleDeleteButtonRecordClick = (playbackRecord) => {
        const requestOptions = {
            method: "GET",
            headers: { "Content-Type": "application/json" }
        };
        fetch(`api/map_playback/delete/${playbackRecord.playback_uuid}/${user.id}`, requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then((records) => {
                setPreviousSubmissions(records);
            })
            .catch((err) => {
                handleFailedFetch(err);
            });
    };
    const handleViewPlaybackFileButtonClick = () => {
        fileInputRef.current.click();
    };
    const handleFileUpload = (event) => {
        if (event.target.files.length > 1 || event.target.files.length === 0) {
            return alert("Please upload a single file");
        }
        const file = event.target.files[0];

        const reader = new FileReader();
        reader.onloadend = async (e) => {
            const arrayBuffer = e.target.result;
            let jsonContent;

            if (file.name.endsWith(".zip") || file.type === "application/zip") {
                const zip = await JSZip.loadAsync(arrayBuffer);

                const jsonFile = Object.values(zip.files).find((f) => {
                    return f.name.endsWith(".json");
                });
                if (!jsonFile) {
                    return alert("No JSON file found inside the ZIP archive.");
                }
                const unzippedText = await jsonFile.async("string");
                jsonContent = JSON.parse(unzippedText);
            } else {
                const decoder = new TextDecoder("utf-8");
                const text = decoder.decode(arrayBuffer);

                jsonContent = JSON.parse(text);
            }
            const isValidFormat = getPlaybackFileValid(jsonContent);
            if (isValidFormat === "") {
                setViewPlaybackFileData(jsonContent);
            } else {
                alert(isValidFormat);
            }
        };
        reader.onerror = (e) => {
            console.error("File reading error:", e.target.error);
        };
        reader.readAsArrayBuffer(file);
    };
    return creatingSubmission ? (
        <MapPlaybackFileCreator setCreatingSubmission={setCreatingSubmission} getPreviousSubmissions={getPreviousSubmissions} />
    ) : viewPlaybackFileData ? (
        <MapPlaybackFileViewer viewPlaybackFileData={viewPlaybackFileData} setViewPlaybackFileData={setViewPlaybackFileData} />
    ) : (
        <Box sx={{ p: 3, height: "100%", width: "100%", display: "flex", flexDirection: "column" }}>
            <Typography component="h2" variant="h6" color="#6b778c" noWrap>
                Map Playback Submissions
            </Typography>

            <Paper sx={{ p: 2, mt: 2, display: "flex", flexDirection: "column", gap: 2, flex: 1 }}>
                <Box sx={{ flex: 1 }}>
                    <DataGrid
                        rows={previousSubmissions}
                        columns={columns}
                        disableRowSelectionOnClick
                        slots={{ pagination: CustomPagination }}
                        getRowId={(row) => row.playback_uuid}
                    />
                </Box>

                <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    onClick={(e) => (e.target.value = "")}
                    multiple
                    style={{ display: "none" }}
                    accept=".json, .zip, application/json, application/zip"
                />

                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
                    <Button variant="contained" onClick={() => handleViewPlaybackFileButtonClick(true)} size="small">
                        View Playback File
                    </Button>
                    <Button variant="contained" onClick={() => setCreatingSubmission(true)} size="small">
                        Request Playback File
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default MapPlaybackDatagrid;
